import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Where an invoice document screen goes when the user is finished with it.
 *
 * **2026-09-09, HMS:** *"When operators create invoices and they save it takes
 * them to 'outgoing Invoice' with the whole list of invoices. 1. Could this
 * stay in the delivery pls 2. outgoing invoices is part of the financials and
 * should not be open for all operators pls."*
 *
 * An operator opened delivery D00000038, raised its invoice, pressed Submit for
 * Approval -- and 1.5 seconds later the screen replaced itself with the Finance
 * department's invoice table. Their delivery was gone and they had to find it
 * again. Every one of the six invoice creator screens did this, from twelve
 * separate hard-coded `history.push('/invoicing/<list>')` calls: submit,
 * approve/reject, the back arrow and the load-failure bail-out.
 *
 * ## The rule this replaces them with
 *
 * **A document screen returns you to where you opened it from, never to a fixed
 * address.** One helper, so a seventh creator screen cannot be written with the
 * old habit.
 *
 * Origin is resolved in this order:
 *
 *   1. An explicit `?returnTo=` in the URL. Survives a refresh and a browser
 *      reload, which route state does not -- so the delivery panel puts it on
 *      the link when it opens an invoice that already exists.
 *   2. `location.state.returnTo`, for callers that would rather not put it in
 *      the address bar.
 *   3. `?deliveryId=` -- the create-from-delivery flow already carries it, so
 *      those six links needed no change at all.
 *   4. **The safety net.** If the person cannot open `listPath` and the
 *      document hangs off a delivery, send them to the delivery. Nobody is
 *      ever pushed at a door that will refuse them -- which is the bug that
 *      started all of this: the redirect landed operators on a Financials
 *      screen, we widened the screen to admit them (2026-09-07), and HMS is
 *      now telling us the redirect was wrong all along.
 *   5. Otherwise the list. A finance user who came in from the invoice table
 *      goes back to the invoice table, exactly as before.
 *
 * Note that the DOCUMENT's own delivery link is only consulted at step 4. A
 * finance user opening a delivery-linked invoice from the list must go back to
 * the list; where the invoice came from is not where the *user* came from.
 */

/** Query-string key carrying an explicit origin. */
export const RETURN_TO_PARAM = 'returnTo';

/**
 * Screens a document may hand the user back to.
 *
 * A whitelist, and a short one on purpose: `returnTo` arrives in the address
 * bar, so it is user-supplied. Without this an emailed link could bounce
 * someone out of HMS entirely (`returnTo=https://…`) or at a screen the sender
 * chose. Protocol-relative `//host` is rejected explicitly -- it starts with
 * `/` and browsers treat it as an absolute URL.
 */
const SAFE_RETURN_PREFIXES = ['/deliveries/', '/invoicing/'];

export const isSafeReturnPath = (path) => (
  typeof path === 'string'
  && path.startsWith('/')
  && !path.startsWith('//')
  && !path.includes('\\')
  && SAFE_RETURN_PREFIXES.some((prefix) => path.startsWith(prefix))
);

/**
 * Can this person open an invoice LIST?
 *
 * Mirrors `ProtectedRoute`'s `hasFinancialAccess` exactly, including the
 * `is_superuser`/`is_staff` widening it shares with every peer gate in HMS. It
 * has to mirror it: granting more here sends someone to a screen that bounces
 * them (the reported bug), granting less sends a finance user to a delivery
 * they never asked for.
 */
export const canOpenInvoiceLists = (user) => Boolean(
  user
  && (
    user.has_full_access
    || user.is_superuser
    || user.is_staff
    || user.has_financial_access
    || user.is_financial_approver
  ),
);

/**
 * @param {Object}  args
 * @param {string}  args.listPath            this document type's list screen
 * @param {Object}  args.user                the signed-in user (AuthContext)
 * @param {number|string|null} args.sourceDeliveryId  the delivery this document
 *                                           hangs off, if any -- used only by
 *                                           the step-4 safety net
 * @param {string|null} args.sourceDeliveryNumber     the D-number, for the label
 * @param {string}  args.listLabel           e.g. "Outgoing Invoices"
 */
const useDocumentReturn = ({
  listPath,
  user,
  sourceDeliveryId = null,
  sourceDeliveryNumber = null,
  listLabel = 'the list',
}) => {
  const history = useHistory();
  const location = useLocation();

  const search = location.search;
  const stateReturnTo = location.state?.returnTo ?? null;

  const returnPath = useMemo(() => {
    const params = new URLSearchParams(search);

    const explicit = params.get(RETURN_TO_PARAM) ?? stateReturnTo;
    if (isSafeReturnPath(explicit)) return explicit;

    const arrivedFromDelivery = params.get('deliveryId');
    if (arrivedFromDelivery) return `/deliveries/${arrivedFromDelivery}`;

    // The safety net. Never push someone at a screen that will refuse them.
    if (!canOpenInvoiceLists(user) && sourceDeliveryId) {
      return `/deliveries/${sourceDeliveryId}`;
    }

    return listPath;
  }, [search, stateReturnTo, user, sourceDeliveryId, listPath]);

  const returnsToDelivery = returnPath.startsWith('/deliveries/');

  const returnLabel = returnsToDelivery
    ? `Back to Delivery ${sourceDeliveryNumber || ''}`.trim()
    : `Back to ${listLabel}`;

  /** The back arrow, and the bail-out when a document fails to load. */
  const goBack = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  /**
   * After a save or an approval.
   *
   * `savedRowId` is what `useSavedRowFlash` reads to scroll the table to the
   * row that was just written -- meaningless on a delivery screen, so it is
   * only attached when the destination is actually a list.
   */
  const goBackAfterSave = useCallback((savedRowId) => {
    if (returnsToDelivery || savedRowId === null || savedRowId === undefined) {
      history.push(returnPath);
      return;
    }
    history.push({ pathname: returnPath, state: { savedRowId } });
  }, [history, returnPath, returnsToDelivery]);

  return { returnPath, returnLabel, returnsToDelivery, goBack, goBackAfterSave };
};

export default useDocumentReturn;
